//@flow
import * as Immutable from 'immutable';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import { MasterHomebase } from 'common/models/flow/MasterHomebase';

import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import { StructuredAddress } from 'common/models/internal/StructuredAddress';
import { TeamId } from 'common/models/internal/TeamId';
import { LatLng } from 'common/models/internal/LatLng';
import { Contact } from 'common/models/internal/Contact';
import { ContactId } from 'common/models/internal/ContactId';
import { TimeSlot } from 'common/models/internal/TimeSlot';
import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import StringUtils from 'common/utils/StringUtils';
import RegExUtil from 'common/utils/RegExUtil';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';

import type {
    MasterHomebaseModelType,
    HomebaseTypeEnum
} from 'common/models/flow/MasterHomebase';
import type { TeamIdModelType } from 'common/models/flow/TeamId';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';

import I18nHelper from 'common/helpers/I18nHelper';
import { ENTITY_NAME_ID } from 'common/constants/entity/homebaseMaster';
import { CrossDockInfo } from './CrossDockInfo';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export const HomebaseStatusEnum = new EntityTypeEnum({
    Active: 'ACTIVE',
    Inactive: 'INACTIVE'
});

export const HomebaseType = new EntityTypeEnum({
    Homebase: 'HOMEBASE',
    Crossdock: 'CROSS_DOCK'
});

export const CrossDocksType = new EntityTypeEnum({
    VehicleToVehicle: 'VTV',
    VehicleWithinVehicle: 'VWV'
});

@validatable
class InternalMasterHomebase extends MasterHomebase {
    static fromJS(json: MasterHomebaseModelType): InternalMasterHomebase {
        const state: Object = Object.assign({}, json);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.address = StructuredAddress.fromJS(state.address);
        state.latLng = LatLng.fromJS(state.latLng);
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.crossDockInfo = state.crossDockInfo
            ? CrossDockInfo.fromJS(state.crossDockInfo)
            : state.crossDockInfo;
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    get latLng(): LatLng {
        return this._state.get('latLng');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    get type(): HomebaseTypeEnum {
        return this._state.get('type');
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    sortTimeSlots(): this {
        return this.setTimeSlots(TimeSlot.sortTimeSlots(this.timeSlots));
    }

    initializeWeekSlots(): this {
        return this.setTimeSlots(
            TimeSlot.initializeAllTimeSlots(this.timeSlots)
        );
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    sanitize(): this {
        const { latLng, contactId, timeSlots, type, crossDockInfo } = this;
        let updatedHomebase = this;
        if (latLng) {
            const sanitizedLatLng = latLng.defaultSanitize();
            updatedHomebase = updatedHomebase.setLatLng(sanitizedLatLng);
        }

        if (!contactId?.contactId) {
            updatedHomebase = updatedHomebase.setContactId(null);
        }

        if (timeSlots) {
            const sanitizedTimeSlots = timeSlots.filter(
                timeSlot => timeSlot.slots.size
            );
            updatedHomebase = updatedHomebase.setTimeSlots(sanitizedTimeSlots);
        }
        if (
            type !== HomebaseType.Crossdock ||
            !crossDockInfo.typesSupported.size
        ) {
            updatedHomebase = updatedHomebase.setCrossDockInfo(null);
        }
        return updatedHomebase.setContact(null);
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        return this.sanitize();
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    static defaultInit({ props }): InternalMasterHomebase {
        const address = StructuredAddress.fromJS({
            countryCode: props.defaultCountryCode
        });
        const latLng = LatLng.defaultInit({ props });
        const crossDockInfo = CrossDockInfo.fromJS({ typesSupported: [] });
        return InternalMasterHomebase.fromJS({})
            .setType(HomebaseType.Homebase)
            .setAddress(address)
            .setLatLng(latLng)
            .setCrossDockInfo(crossDockInfo);
    }

    @softValidateAll
    hasValidationErrors(): ?boolean {
        const { address, latLng } = this;
        if (address && address.hasValidationErrors()) {
            return true;
        }
        if (latLng && latLng.hasValidationErrors()) {
            return true;
        }
        return this.validateId() || this.validateName();
    }

    @validateAll
    setAllValidationErrors(): this {
        let { address, latLng, timeSlots } = this;
        if (address) {
            address = address.setAllValidationErrors();
        } else {
            //validation when address completely is empty
            address = StructuredAddress.fromJS({}).setAllValidationErrors();
        }
        if (latLng) {
            latLng = latLng.setAllValidationErrors();
        }
        if (timeSlots) {
            timeSlots = timeSlots.map(timeSlot =>
                timeSlot.setAllValidationErrors()
            );
        }
        return this.setAddress(address)
            .setLatLng(latLng)
            .setTimeSlots(timeSlots);
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName(): ?string {
        if (
            StringUtils.isEmpty(this.name) ||
            StringUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setType(type: HomebaseTypeEnum): this {
        return super.setType(type);
    }

    validateType() {
        const { type } = this;
        const accessTypeValues = HomebaseType.values();
        const fieldType = nsTranslate('masterHomebaseProperties.type');
        if (DataUtils.isEmpty(type)) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType
            });
        }
        if (!accessTypeValues.includes(type)) {
            const typeList = HomebaseType.getOptions({
                translationKey: 'masterHomebaseProperties.types'
            })
                .map(({ label }) => label)
                .join('/');
            return nsTranslate('entityValidationErrors.oneOfType', {
                typeList,
                fieldType
            });
        }
    }

    @validate
    setTransactionDuration(transactionDuration: number): this {
        return super.setTransactionDuration(transactionDuration);
    }

    validateTransactionDuration() {
        if (
            !DataUtils.isEmpty(this.transactionDuration) &&
            !NumberUtils.isWholeNumber(this.transactionDuration)
        ) {
            return nsTranslate(
                'homebaseValidationErrors.transactionDurationNotAWholeNumber'
            );
        }
    }

    @validate
    setLat(lat: number): this {
        return super.setLatLng(this.latLng.setLat(lat));
    }

    validateLat() {
        if (DataUtils.isEmpty(this.latLng.lat)) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType: nsTranslate('masterHomebaseProperties.latitude')
            });
        }
        return this.latLng.validateLat();
    }

    @validate
    setLng(lng: number): this {
        return super.setLatLng(this.latLng.setLng(lng));
    }

    validateLng() {
        if (DataUtils.isEmpty(this.latLng.lng)) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType: nsTranslate('masterHomebaseProperties.longitude')
            });
        }
        return this.latLng.validateLng();
    }

    @validate
    setTeams(teams: Array<TeamIdModelType>): this {
        return super.setTeams(teams);
    }

    validateTeams(): ?string {
        if (!this.teams.size) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType: nsTranslate('masterHomebaseProperties.teams')
            });
        }
    }

    @validate
    setCrossDockInfo(crossDockInfo: ?CrossDockInfo): this {
        return this.clone(this._state.set('crossDockInfo', crossDockInfo));
    }

    validateCrossDockInfo(): ?string {
        const { crossDockInfo, type } = this;
        const crossDockValues = CrossDocksType.values();
        if (type === HomebaseType.Crossdock && crossDockInfo) {
            const { typesSupported } = crossDockInfo;
            if (
                !typesSupported.every(crossDock =>
                    crossDockValues.includes(crossDock)
                )
            ) {
                const translatedTypesSupported = CrossDocksType.getOptions({
                    translationKey: 'masterHomebaseProperties.crossDockTypes'
                })
                    .map(({ label }) => label)
                    .join('/');
                return nsTranslate(
                    'homebaseValidationErrors.invalidCrossDockType',
                    { types: translatedTypesSupported }
                );
            }
            if (typesSupported.isEmpty()) {
                return nsTranslate(
                    'homebaseValidationErrors.crossDockTypeNotPresent'
                );
            }

            if (
                !crossDockInfo.typesSupported.includes(
                    CrossDocksType.VehicleToVehicle
                ) &&
                (!DataUtils.isEmpty(crossDockInfo.taskLimit?.lowerThresh) ||
                    !DataUtils.isEmpty(crossDockInfo.taskLimit?.higherThresh))
            ) {
                return nsTranslate(
                    'homebaseValidationErrors.thresholdNotSupported'
                );
            }
        }
    }

    setContactAndContactId(contact: ?Contact): this {
        let updatedHomebase = super.setContact(contact);
        if (contact) {
            const { clientId, id: contactId, version } = contact;
            updatedHomebase = updatedHomebase.setContactId(
                ContactId.fromJS({
                    clientId,
                    contactId,
                    version
                })
            );
        } else {
            updatedHomebase = updatedHomebase.setContactId(null);
        }

        return updatedHomebase;
    }

    get crossDockInfo(): ?CrossDockInfo {
        return this._state.get('crossDockInfo');
    }

    getTodayTimeSlots(): ?Immutable.List<TimeSlot> {
        return MultiDaySlot.getTodayTimeSlots(this.timeSlots);
    }
}

export { InternalMasterHomebase as MasterHomebase };
